/**
 * @engine/core - Content passes (sprites, deferred accumulation, particles)
 *
 * The shared geometry/compositing passes used by both the editor and game viewports. Sprite collection +
 * drawing is delegated to the {@link SceneSpriteCollector} on the context; lighting buffers come from the
 * blackboard (published by the Lighting-stage passes).
 *
 * @module @engine/core
 */

import type { LightCullPass, ShadowMapPass } from '@engine/render2d';
import { Profiler } from '../../Profiler';
import { ViewportPass, RenderStage, BB, type ViewportPassContext } from '../RenderPipeline';

function lightCullOf(c: ViewportPassContext): LightCullPass | null {
  return (c.blackboard.get(BB.lightCull) as LightCullPass | null) ?? null;
}
function shadowMapOf(c: ViewportPassContext): ShadowMapPass | null {
  return (c.blackboard.get(BB.shadowMap) as ShadowMapPass | null) ?? null;
}

/**
 * Deferred GBuffer pass: opaque sprites → MRT (albedo/normal/emission + depth). Transparent + material sprites
 * are partitioned out and left on the collector for {@link TransparentSpritesPass}. Publishes the depth texture.
 */
export class GBufferOpaquePass extends ViewportPass {
  readonly stage = RenderStage.Opaque;
  readonly profileName = 'Render.GBuffer';

  execute(c: ViewportPassContext): void {
    if (!c.level || !c.lighting) return;
    c.lighting.pipeline.beginGBufferPass(c.ctx);

    Profiler.begin('Render.Sprites.Collect');
    c.sprites.collect(c.level, c.spriteDeps);
    Profiler.end('Render.Sprites.Collect');

    c.sprites.partitionForGBuffer();
    c.sprites.drawOpaqueToGBuffer(c.spriteDeps);

    c.blackboard.set(BB.gbufferDepth, c.lighting.pipeline.gbufferTarget.depthTexture);
  }
}

/** Deferred light accumulation: fullscreen quad reads GBuffer + lights → color target. */
export class DeferredAccumPass extends ViewportPass {
  readonly stage = RenderStage.Accumulation;
  readonly profileName = 'Render.LightAccum';

  execute(c: ViewportPassContext): void {
    if (!c.lighting) return;
    c.lighting.pipeline.renderAccumulation(
      c.lighting.lightManager,
      c.colorTarget,
      c.isHdrTarget,
      c.ctx,
      lightCullOf(c)?.tileLightDataBuffer,
      shadowMapOf(c)?.shadowMapBuffer,
      c.lighting.iesProfileBuffer ?? undefined,
    );
  }
}

/** Forward+ only path: all sprites drawn lit directly to the color target (no GBuffer). */
export class ForwardPlusOpaquePass extends ViewportPass {
  readonly stage = RenderStage.Opaque;
  readonly profileName = 'Render.ForwardLitContent';

  execute(c: ViewportPassContext): void {
    if (!c.level || !c.lighting) return;
    const lightCull = lightCullOf(c);
    const shadowMap = shadowMapOf(c);

    c.beginColorPass();
    if (c.ctx.setForwardPlusData && lightCull?.isReady) {
      c.ctx.setForwardPlusData(
        c.lighting.lightManager.uniformBuffer,
        c.lighting.lightManager.lightBuffer,
        lightCull.tileLightDataBuffer,
        shadowMap?.shadowMapBuffer,
        c.lighting.iesProfileBuffer ?? undefined,
      );
    }

    Profiler.begin('Render.Sprites.Collect');
    c.sprites.collect(c.level, c.spriteDeps);
    Profiler.end('Render.Sprites.Collect');

    c.sprites.drawForwardLit(c.sprites.all, c.spriteDeps, {
      lightManager: c.lighting.lightManager,
      lightCullPass: lightCull,
      shadowMapBuffer: shadowMap?.shadowMapBuffer,
      iesProfileBuffer: c.lighting.iesProfileBuffer,
      isHdrTarget: c.isHdrTarget,
    });
  }
}

/** Unlit fallback path: all sprites drawn unlit to the color target. */
export class UnlitOpaquePass extends ViewportPass {
  readonly stage = RenderStage.Opaque;
  readonly profileName = 'Render.Content';

  execute(c: ViewportPassContext): void {
    if (!c.level) return;
    c.beginColorPass();
    Profiler.begin('Render.Sprites.Collect');
    c.sprites.collect(c.level, c.spriteDeps);
    Profiler.end('Render.Sprites.Collect');
    c.sprites.drawUnlit(c.sprites.all, c.spriteDeps, c.isHdrTarget);
  }
}

/** Deferred path: transparent + material sprites drawn forward-lit over the accumulated color. */
export class TransparentSpritesPass extends ViewportPass {
  readonly stage = RenderStage.Transparent;
  readonly profileName = 'Render.ForwardLit';

  execute(c: ViewportPassContext): void {
    if (!c.lighting || !c.sprites.transparent.length) return;
    c.beginColorPass();
    c.sprites.drawForwardLit(c.sprites.transparent, c.spriteDeps, {
      lightManager: c.lighting.lightManager,
      lightCullPass: lightCullOf(c),
      shadowMapBuffer: shadowMapOf(c)?.shadowMapBuffer,
      iesProfileBuffer: c.lighting.iesProfileBuffer,
      isHdrTarget: c.isHdrTarget,
    });
  }
}

/** VFX particle rendering (play mode). Binds Forward+ data so lit particles see the light tiles. */
export class ParticlesPass extends ViewportPass {
  readonly stage = RenderStage.AfterTransparent;
  readonly profileName = 'Render.VFX';
  private vp = new Float32Array(16);

  execute(c: ViewportPassContext): void {
    if (!c.callbacks.isPlaying || !c.callbacks.vfxRender) return;
    c.beginColorPass();
    const lightCull = lightCullOf(c);
    if (c.lighting && lightCull?.isReady && c.ctx.setForwardPlusData) {
      c.ctx.setForwardPlusData(
        c.lighting.lightManager.uniformBuffer,
        c.lighting.lightManager.lightBuffer,
        lightCull.tileLightDataBuffer,
        shadowMapOf(c)?.shadowMapBuffer,
        c.lighting.iesProfileBuffer ?? undefined,
      );
    }
    c.camera.getViewProjection(this.vp);
    c.callbacks.vfxRender(c.ctx, this.vp, c.isHdrTarget);
  }
}
